import { apiCall } from './client';

export interface Transaction {
  id: string;
  user_id: string;
  date: string;
  amount: number;
  type: 'spend' | 'income' | 'savings' | 'investment';
  category: string;
  note?: string;
  recurrence: string;
  credit_card_id?: string;
  created_at: string;
  updated_at: string;
}

export interface TransactionCreate {
  date: string;
  amount: number;
  type: string;
  category: string;
  note?: string;
  recurrence?: string;
  credit_card_id?: string;
}

export interface CreditCard {
  id: string;
  name: string;
  last_four?: string;
  credit_limit?: number;
  billing_day?: number;
  annual_fee: number;
  fee_month?: number;
  waiver_days_before: number;
  created_at: string;
  updated_at: string;
}

export interface BankAccount {
  id: string;
  name: string;
  account_number?: string;
  balance: number;
  updated_at: string;
}

export interface MonthlyTarget {
  id: string;
  year: number;
  month: number;
  category: string;
  target_amount: number;
}

export interface Dashboard {
  total_spend: number;
  total_income: number;
  total_savings: number;
  total_invested: number;
  net_savings: number;
  top_categories: { category: string; total: number }[];
  monthly_history: { year: number; month: number; total: number }[];
  credit_card_alerts: string[];
  predicted_next_month?: number;
}

export interface Categories {
  spend: string[];
  income: string[];
  savings: string[];
  investment: string[];
}

// Transactions
export const getTransactions = (params?: Record<string, string>) => {
  const qs = params ? '?' + new URLSearchParams(params).toString() : '';
  return apiCall<Transaction[]>(`/api/v1/budget/transactions${qs}`);
};
export const createTransaction = (body: TransactionCreate) =>
  apiCall<Transaction>('/api/v1/budget/transactions', { method: 'POST', body: JSON.stringify(body) });
export const updateTransaction = (id: string, body: Partial<TransactionCreate>) =>
  apiCall<Transaction>(`/api/v1/budget/transactions/${id}`, { method: 'PUT', body: JSON.stringify(body) });
export const deleteTransaction = (id: string) =>
  apiCall<void>(`/api/v1/budget/transactions/${id}`, { method: 'DELETE' });

// Credit Cards
export const getCreditCards = () => apiCall<CreditCard[]>('/api/v1/budget/credit-cards');
export const createCreditCard = (body: Omit<CreditCard, 'id' | 'created_at' | 'updated_at'>) =>
  apiCall<CreditCard>('/api/v1/budget/credit-cards', { method: 'POST', body: JSON.stringify(body) });
export const updateCreditCard = (id: string, body: Partial<CreditCard>) =>
  apiCall<CreditCard>(`/api/v1/budget/credit-cards/${id}`, { method: 'PUT', body: JSON.stringify(body) });
export const deleteCreditCard = (id: string) =>
  apiCall<void>(`/api/v1/budget/credit-cards/${id}`, { method: 'DELETE' });

// Bank Accounts
export const getBankAccounts = () => apiCall<BankAccount[]>('/api/v1/budget/bank-accounts');
export const createBankAccount = (body: Omit<BankAccount, 'id' | 'updated_at'>) =>
  apiCall<BankAccount>('/api/v1/budget/bank-accounts', { method: 'POST', body: JSON.stringify(body) });
export const updateBankAccount = (id: string, body: Partial<BankAccount>) =>
  apiCall<BankAccount>(`/api/v1/budget/bank-accounts/${id}`, { method: 'PUT', body: JSON.stringify(body) });
export const deleteBankAccount = (id: string) =>
  apiCall<void>(`/api/v1/budget/bank-accounts/${id}`, { method: 'DELETE' });

// Dashboard
export const getDashboard = (params?: { from?: string; to?: string; predict?: boolean }) => {
  const qs = params ? '?' + new URLSearchParams(Object.entries(params).filter(([, v]) => v !== undefined).map(([k, v]) => [k, String(v)])).toString() : '';
  return apiCall<Dashboard>(`/api/v1/budget/dashboard${qs}`);
};

// Categories
export const getCategories = () => apiCall<Categories>('/api/v1/budget/categories');

// Monthly targets
export const getTargets = (year: number, month: number) =>
  apiCall<MonthlyTarget[]>(`/api/v1/budget/targets?year=${year}&month=${month}`);
export const upsertTarget = (body: { year: number; month: number; category: string; target_amount: number }) =>
  apiCall<MonthlyTarget>('/api/v1/budget/targets', { method: 'POST', body: JSON.stringify(body) });
export const getTargetSummary = (year: number, month: number) =>
  apiCall<{ category: string; spent: number; target: number; remaining: number; over_budget: boolean }[]>(
    `/api/v1/budget/targets/summary?year=${year}&month=${month}`
  );
